import { useState } from 'react';
import { Flex, Space } from 'antd';
import { PromptGenerateArea } from './promptGenerateArea';
import { WriteFlowDropdown } from './promptMenu/writeFlowDropdown';
import { EditFlowDropdown } from './promptMenu/editFlowDropdown';
import { useSmartReplyPromptPresets } from '@copilot/common/hooks/smartReply/smartReplyPromptPresets/useSmartReplyPromptPresetContext';
import Popover from '@copilot/common/components/containers/tooltips/popover';
import styles from './smartReply.module.less';
import { PromptMenuHeader } from './promptMenu/promptMenu';
import { isNil } from 'lodash';
import { AI_EDITS_BUTTON_TEXT, SMART_REPLY_BUTTON_TEXT } from './constants';
import {
    SMART_REPLY_COMMUNICATION_STYLES_TOGGLE_TRACKING_ID,
    SMART_REPLY_TRACKING_ID,
} from '@copilot/common/tracking/userpilotEventConsts';
import { AiButton } from '@copilot/common/components/ai/aiButton/aiButton';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';
import {
    WRITE_BUTTON_TEXT,
    WRITE_PLACEHOLDER,
    EDIT_BUTTON_TEXT,
    EDIT_PLACEHOLDER,
} from './smartReplyConstants';
import { AiIcon } from '@copilot/common/components/ai/aiIcon/aiIcon';
import { SmartReplyPromptPresetCategory } from '@copilot/common/hooks/smartReply/smartReplyPromptPresets/smartReplyPromptTypes';
import { AiSwitch } from '@copilot/common/components/ai/aiSwitch/aiSwitch';

/** Interface Smart Reply Button Props */
export type SmartReplyButtonProps = Readonly<{
    /**
     * Whether or not the parents text area has a message
     */
    hasMessage: boolean;
    /**
     * Update whether the AI assistant is currently active
     */
    setIsActive: (update: boolean) => void;
    /**
     * Whether the ai assistant is currently active
     */
    isActive: boolean;
    /**
     * Whether the user can regenerate
     */
    canRegenerate: boolean;
    /**
     * Callback to retry an AI query
     */
    onRegenerate: () => void;
    /**
     * Function called when user creates a custom prompt
     */
    onSubmitCustomPrompt: (
        prompt: string,
        isEditing: boolean,
        includeCommunicationStyles: boolean
    ) => void;
    /**
     * Function called when user selects a preset prompt
     */
    onSelectPreset: (promptKey: string, includeCommunicationStyles: boolean) => void;
    /**
     * Whether the button is disabled
     */
    disabled?: boolean;
    /**
     * The prompt category that currently applies to the conversation
     */
    smartReplyPromptCategory?: SmartReplyPromptPresetCategory;
    /**
     * Whether the user can use communication styles
     */
    canUseCommunicationStyles: boolean;
}>;

/**
 * Component to represent the Smart Reply Button
 * @param props
 */
export function SmartReplyButton({
    hasMessage,
    isActive,
    setIsActive,
    onSubmitCustomPrompt,
    onSelectPreset,
    disabled,
    smartReplyPromptCategory,
    canUseCommunicationStyles,
}: SmartReplyButtonProps) {
    const { isNewCommStyleSmartReplyEnabled } = useFeatureFlags();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isWritingCustomPrompt, setIsWritingCustomPrompt] = useState<boolean>(false);

    const [includeCommunicationStyles, setIncludeCommunicationStyles] = useState(true);

    const { writePrompts, editPrompts } = useSmartReplyPromptPresets();

    const suggestedPrompts = writePrompts.map((prompt) => {
        return {
            ...prompt,
            isSuggested: !isNil(smartReplyPromptCategory)
                ? prompt.useCases?.includes(smartReplyPromptCategory)
                : false,
        };
    });

    // Popover switching for Writing A Prompt
    const smartReplyWriteFlow = isWritingCustomPrompt ? (
        <PromptGenerateArea
            onSubmitCustomPrompt={(prompt) =>
                onSubmitCustomPrompt(
                    prompt,
                    isEditing,
                    includeCommunicationStyles && canUseCommunicationStyles
                )
            }
            onCancel={() => {
                setIsWritingCustomPrompt(false);
                setIsActive(false);
            }}
            placeholderText={WRITE_PLACEHOLDER}
            submitButtonText={WRITE_BUTTON_TEXT}
        />
    ) : (
        <WriteFlowDropdown
            prompts={suggestedPrompts}
            onSelectPreset={(promptKey) =>
                onSelectPreset(promptKey, includeCommunicationStyles && canUseCommunicationStyles)
            }
            setIsWritingCustomPrompt={setIsWritingCustomPrompt}
        />
    );

    // Popover switching for Editing a message with prompt
    const smartReplyEditFlow = isWritingCustomPrompt ? (
        <PromptGenerateArea
            onSubmitCustomPrompt={(prompt) =>
                onSubmitCustomPrompt(
                    prompt,
                    isEditing,
                    includeCommunicationStyles && canUseCommunicationStyles
                )
            }
            onCancel={() => {
                setIsWritingCustomPrompt(false);
                setIsEditing(false);
                setIsActive(false);
            }}
            placeholderText={isEditing ? EDIT_PLACEHOLDER : WRITE_PLACEHOLDER}
            submitButtonText={isEditing ? EDIT_BUTTON_TEXT : WRITE_BUTTON_TEXT}
        />
    ) : (
        <>
            <PromptMenuHeader copy="Edit a message with AI" />
            <EditFlowDropdown
                prompts={editPrompts}
                onSelectPreset={(promptKey) =>
                    onSelectPreset(promptKey, includeCommunicationStyles)
                }
                setIsWritingCustomPrompt={setIsWritingCustomPrompt}
                setIsEditing={setIsEditing}
            />

            <PromptMenuHeader copy="Write a message with AI" />
            <WriteFlowDropdown
                prompts={writePrompts}
                onSelectPreset={(promptKey) =>
                    onSelectPreset(promptKey, includeCommunicationStyles)
                }
                setIsWritingCustomPrompt={setIsWritingCustomPrompt}
            />
        </>
    );

    const popoverContent = hasMessage ? smartReplyEditFlow : smartReplyWriteFlow;

    return (
        <Space size={'small'}>
            <Popover
                content={
                    <Flex vertical>
                        {isNewCommStyleSmartReplyEnabled && (
                            <Flex gap={6} className={styles.smartReplyPopoverHeader}>
                                <div>
                                    {canUseCommunicationStyles
                                        ? "Apply this prospect's communication style preference"
                                        : "Prospect's communication style currently unavailable"}
                                </div>
                                <AiIcon noSpace />
                                <AiSwitch
                                    data-tracking-id={
                                        SMART_REPLY_COMMUNICATION_STYLES_TOGGLE_TRACKING_ID
                                    }
                                    disabled={!canUseCommunicationStyles}
                                    checked={
                                        includeCommunicationStyles && canUseCommunicationStyles
                                    }
                                    onChange={(checked) => setIncludeCommunicationStyles(checked)}
                                />
                            </Flex>
                        )}
                        {popoverContent}
                    </Flex>
                }
                trigger="click"
                placement="bottomLeft"
                open={disabled === true ? false : undefined}
                className={styles.smartReplyPopover}
            >
                <AiButton
                    isDropdownTrigger
                    onClick={() => {
                        setIsActive(!isActive);
                    }}
                    disabled={disabled}
                    data-tracking-id={SMART_REPLY_TRACKING_ID}
                >
                    {hasMessage ? AI_EDITS_BUTTON_TEXT : SMART_REPLY_BUTTON_TEXT}
                </AiButton>
            </Popover>
        </Space>
    );
}
